<template>
    <div class="titulo-projeto-linha" ref="raiz">
        <div class="titulo-projeto" ref="ancora">
            <h2>
                <button type="button" class="titulo-projeto__gatilho" @click="alternarLista">
                    <span v-if="codigo">{{ codigo + ' - ' }}</span>{{ nome || '-' }}
                </button>
            </h2>
            <ul v-if="listaAberta" class="titulo-projeto__lista" role="listbox">
                <li v-if="carregando" class="titulo-projeto__vazio">Carregando...</li>
                <li v-else-if="antecessores.length === 0" class="titulo-projeto__vazio">Nenhum antecessor vinculado</li>
                <li v-for="item in antecessores" :key="item.id">
                    <button type="button" class="titulo-projeto__opcao acao-secundaria"
                        :class="{ selecionado: Number(item.id) === Number(antecessorAbertoId) }"
                        @click="escolher(item)">
                        <span>{{ rotulo(item) }}</span>
                        <small v-if="item.empresa_nome">{{ item.empresa_nome }}</small>
                    </button>
                </li>
            </ul>
        </div>
        <button v-if="ehRoot" type="button" class="titulo-projeto__vincular acao-secundaria"
            title="Vincular antecessor" @click="alternarPainel">
            <i class="bi-link-45deg"></i>
        </button>
        <div v-if="visualizando" class="titulo-projeto__contexto">
            <button type="button" class="titulo-projeto__voltar" :title="'Voltar para ' + rotuloOrigem"
                @click="$emit('voltar')">
                <i class="bi-arrow-return-left"></i>
                <span class="titulo-projeto__voltar-texto">
                    <small>Voltar para</small>
                    <strong>{{ rotuloOrigem }}</strong>
                </span>
            </button>
            <span v-if="!podeEditar" class="titulo-projeto__selo">
                <i class="bi-eye"></i> Somente leitura
            </span>
        </div>
        <div v-if="painelAberto" class="titulo-projeto__painel" ref="painel">
            <div class="titulo-projeto__painel-cabecalho">
                <strong>Antecessores do projeto</strong>
                <button type="button" class="titulo-projeto__fechar" title="Fechar" @click="painelAberto = false">
                    <i class="bi-x-lg"></i>
                </button>
            </div>
            <div class="titulo-projeto__busca">
                <i class="bi-search"></i>
                <input type="search" v-model="termo" placeholder="Buscar por código ou nome (qualquer empresa)"
                    @input="agendarBusca" />
            </div>
            <p v-if="erro" class="titulo-projeto__erro"><i class="bi-exclamation-circle"></i> {{ erro }}</p>
            <ul v-if="resultados.length" class="titulo-projeto__itens">
                <li v-for="item in resultados" :key="item.id">
                    <div class="titulo-projeto__item-texto">
                        <span>{{ rotulo(item) }}</span>
                        <small v-if="item.empresa_nome"><i class="bi-building"></i> {{ item.empresa_nome }}</small>
                    </div>
                    <button type="button" class="titulo-projeto__acao titulo-projeto__acao--vincular"
                        @click="vincular(item)" :disabled="salvando">
                        <i class="bi-plus-lg"></i> Vincular
                    </button>
                </li>
            </ul>
            <p v-else-if="termo.trim().length >= 2" class="titulo-projeto__dica">Nenhum projeto encontrado.</p>
            <p v-else class="titulo-projeto__dica">Digite ao menos 2 caracteres para buscar.</p>

            <div class="titulo-projeto__secao">Vinculados ({{ antecessores.length }})</div>
            <ul v-if="antecessores.length" class="titulo-projeto__itens">
                <li v-for="item in antecessores" :key="'v-' + item.id">
                    <div class="titulo-projeto__item-texto">
                        <span>{{ rotulo(item) }}</span>
                        <small v-if="item.empresa_nome"><i class="bi-building"></i> {{ item.empresa_nome }}</small>
                    </div>
                    <button type="button" class="titulo-projeto__acao titulo-projeto__acao--remover"
                        title="Remover vínculo" @click="desvincular(item)" :disabled="salvando">
                        <i class="bi-trash3"></i>
                    </button>
                </li>
            </ul>
            <p v-else class="titulo-projeto__dica">Nenhum antecessor vinculado ainda.</p>
        </div>
    </div>
</template>

<script>
import serviceProjetos from '@/services/serviceProjetos'
import { usuarioEhRoot } from 'roboflex-thalamus-request-handler'

export default {
    name: 'TituloProjetoAntecessor',
    props: {
        projetoId: { required: true },
        nome: { type: String, default: '' },
        codigo: { type: String, default: '' },
        nomeOrigem: { type: String, default: '' },
        codigoOrigem: { type: String, default: '' },
        visualizando: { type: Boolean, default: false },
        antecessorAbertoId: { default: null },
        podeEditar: { type: Boolean, default: false },
    },
    emits: ['selecionar', 'voltar'],
    data() {
        return {
            listaAberta: false,
            painelAberto: false,
            carregando: false,
            salvando: false,
            antecessores: [],
            resultados: [],
            termo: '',
            erro: '',
            buscaTimer: null,
        }
    },
    computed: {
        ehRoot() {
            return usuarioEhRoot()
        },
        rotuloOrigem() {
            if (this.codigoOrigem && this.nomeOrigem) return `${this.codigoOrigem} - ${this.nomeOrigem}`
            return this.nomeOrigem || this.codigoOrigem || 'projeto atual'
        },
    },
    watch: {
        projetoId() {
            this.listaAberta = false
            this.painelAberto = false
            this.carregar()
        },
    },
    mounted() {
        document.addEventListener('click', this.fecharAoClicarFora)
        this.carregar()
    },
    beforeUnmount() {
        document.removeEventListener('click', this.fecharAoClicarFora)
        clearTimeout(this.buscaTimer)
    },
    methods: {
        rotulo(item) {
            if (item?.codigo && item?.nome) return `${item.codigo} - ${item.nome}`
            return item?.nome || item?.codigo || 'Projeto'
        },
        async carregar() {
            if (!this.projetoId || this.projetoId === 'criar') {
                this.antecessores = []
                return
            }
            this.carregando = true
            try {
                this.antecessores = await serviceProjetos.listarAntecessores(this.projetoId)
            } catch {
                this.antecessores = []
            } finally {
                this.carregando = false
            }
        },
        alternarLista() {
            this.painelAberto = false
            this.listaAberta = !this.listaAberta
        },
        alternarPainel() {
            this.listaAberta = false
            this.painelAberto = !this.painelAberto
            this.erro = ''
            this.resultados = []
            this.termo = ''
        },
        escolher(item) {
            this.listaAberta = false
            if (Number(item.id) === Number(this.antecessorAbertoId)) return
            this.$emit('selecionar', item)
        },
        fecharAoClicarFora(event) {
            if (this.$refs.raiz?.contains(event.target)) return
            this.listaAberta = false
            this.painelAberto = false
        },
        agendarBusca() {
            clearTimeout(this.buscaTimer)
            this.buscaTimer = setTimeout(() => this.buscar(), 300)
        },
        async buscar() {
            const termo = this.termo.trim()
            if (termo.length < 2) {
                this.resultados = []
                return
            }
            this.erro = ''
            try {
                this.resultados = await serviceProjetos.buscarProjetosParaVinculo(this.projetoId, termo)
            } catch (error) {
                this.resultados = []
                this.erro = error?.response?.data?.message || 'Não foi possível buscar projetos.'
            }
        },
        async vincular(item) {
            this.salvando = true
            this.erro = ''
            try {
                await serviceProjetos.vincularAntecessor(this.projetoId, item.id)
                this.termo = ''
                this.resultados = []
                await this.carregar()
            } catch (error) {
                this.erro = error?.response?.data?.message || 'Não foi possível vincular o projeto.'
            } finally {
                this.salvando = false
            }
        },
        async desvincular(item) {
            if (!window.confirm(`Remover o vínculo com ${this.rotulo(item)}?`)) return
            this.salvando = true
            this.erro = ''
            try {
                await serviceProjetos.desvincularAntecessor(this.projetoId, item.id)
                await this.carregar()
            } catch (error) {
                this.erro = error?.response?.data?.message || 'Não foi possível remover o vínculo.'
            } finally {
                this.salvando = false
            }
        },
    },
}
</script>

<style scoped>
.titulo-projeto-linha {
    position: relative;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    min-width: 0;
}

.titulo-projeto {
    position: relative;
    min-width: 0;
}

.titulo-projeto h2 {
    margin: 0;
}

.titulo-projeto__gatilho {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
    text-align: left;
    cursor: pointer;
}

button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
}

button:disabled {
    opacity: 0.5;
    cursor: default;
}

/* Lista do título e painel de vínculo */
.titulo-projeto__lista,
.titulo-projeto__painel {
    position: absolute;
    z-index: 30;
    top: calc(100% + 6px);
    left: 0;
    background: var(--cor-bg);
    border: 1px solid var(--cor-separador);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.titulo-projeto__lista {
    min-width: 20rem;
    max-width: 34rem;
    max-height: 20rem;
    overflow: auto;
    margin: 0;
    padding: 0.35rem;
    list-style: none;
}

button.titulo-projeto__opcao {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 100%;
    text-align: left;
    padding: 0.45rem 0.75rem;
    border: 0;
    border-radius: 0;
    background: none;
    color: inherit;
    font-weight: 400;
    box-shadow: none;
}

button.titulo-projeto__opcao:hover,
button.titulo-projeto__opcao.selecionado {
    background: var(--cor-cinza2);
    color: inherit;
}

.titulo-projeto small {
    font-size: 12px;
    color: var(--cor-fonte-fraca);
}

.titulo-projeto__vazio {
    padding: 0.55rem 0.75rem;
    color: var(--cor-fonte-fraca);
}

/* Botão de vincular (root): só o ícone */
button.titulo-projeto__vincular {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: auto;
    margin: 0;
    padding: 0 2px;
    border: 0;
    border-radius: 0;
    background: none;
    box-shadow: none;
    color: var(--cor-fonte-fraca);
    font-size: 18px;
    font-weight: 400;
    line-height: 1;
}

button.titulo-projeto__vincular:hover {
    color: var(--cor-primaria-forte);
    background: none;
    border: 0;
}

/* Voltar fica na linha de baixo, alinhado ao título */
.titulo-projeto__contexto {
    flex: 1 0 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin: 0;
}

.titulo-projeto__voltar {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    max-width: 22rem;
    padding: 4px 14px 4px 10px;
    border-radius: 999px;
    border: 1px solid var(--cor-primaria-media);
    background: var(--cor-primaria-fraca);
    color: var(--cor-primaria-forte);
    transition: all 120ms ease-in-out;
}

.titulo-projeto__voltar:hover {
    background: var(--cor-primaria-media);
}

.titulo-projeto__voltar > i {
    font-size: 16px;
}

.titulo-projeto__voltar-texto {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    line-height: 1.15;
}

.titulo-projeto__voltar-texto small {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.75;
}

.titulo-projeto__voltar-texto strong {
    max-width: 18rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
}

.titulo-projeto__selo {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--cor-cinza2);
    color: var(--cor-fonte-fraca);
    font-size: 12px;
}

/* Painel de vínculo */
.titulo-projeto__painel {
    width: 26rem;
    max-width: calc(100vw - 2rem);
    padding: 0.9rem;
}

.titulo-projeto__painel-cabecalho {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.titulo-projeto__fechar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: var(--cor-fonte-fraca);
}

.titulo-projeto__fechar:hover {
    background: var(--cor-cinza2);
}

.titulo-projeto__busca {
    position: relative;
}

.titulo-projeto__busca i {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--cor-fonte-fraca);
    pointer-events: none;
}

.titulo-projeto__busca input {
    width: 100%;
    margin: 0;
    padding-left: 32px;
}

.titulo-projeto__secao {
    margin: 0.9rem 0 0.35rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--cor-separador);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--cor-fonte-fraca);
}

.titulo-projeto__itens {
    list-style: none;
    margin: 0.5rem 0 0;
    padding: 0;
    max-height: 12rem;
    overflow: auto;
}

.titulo-projeto__itens li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.45rem 0.5rem;
    border-radius: 6px;
}

.titulo-projeto__itens li:hover {
    background: var(--cor-cinza);
}

.titulo-projeto__item-texto {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.titulo-projeto__item-texto span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.titulo-projeto__item-texto small {
    font-size: 12px;
    color: var(--cor-fonte-fraca);
}

.titulo-projeto__acao {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border-radius: 6px;
    font-size: 13px;
    transition: all 120ms ease-in-out;
}

.titulo-projeto__acao--vincular {
    padding: 4px 10px;
    color: var(--cor-primaria-forte);
    border: 1px solid var(--cor-primaria-media);
}

.titulo-projeto__acao--vincular:hover:not(:disabled) {
    background: var(--cor-primaria-fraca);
}

.titulo-projeto__acao--remover {
    justify-content: center;
    width: 30px;
    height: 30px;
    color: var(--cor-fonte-fraca);
}

.titulo-projeto__acao--remover:hover:not(:disabled) {
    color: var(--cor-erro);
    background: var(--cor-cinza2);
}

.titulo-projeto__dica {
    margin: 0.6rem 0 0;
    font-size: 13px;
    color: var(--cor-fonte-fraca);
}

.titulo-projeto__erro {
    margin: 0.6rem 0 0;
    font-size: 13px;
    color: var(--cor-erro);
}
</style>

